import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnsType } from 'antd/es/table';
import { Table } from '@/ui';
import { formatPercentValue } from '@/utils';
import { ICancelledReasonItem } from '@/models/cancelledOrders.model';
import DynamicsCell from '../DynamicsCell/DynamicsCell';
import { StyledFooterButton, StyledTableCard, StyledTableTitle } from './ReasonsTableStyles';
import InfoLabel from '@/modules/Dashboard/components/InfoLabel';

interface IReasonsTableProps {
  items: ICancelledReasonItem[];
  loading?: boolean;
  onViewAll?: () => void;
  limit?: number;
}

const ReasonsTable = ({ items, loading, onViewAll, limit = 10 }: IReasonsTableProps) => {
  const { t, i18n } = useTranslation(['dashboard', 'orders']);
  const ppSuffix = t('cancelled_orders_pp_suffix');

  const dataSource = useMemo(() => items.slice(0, limit), [items, limit]);

  const columns: ColumnsType<ICancelledReasonItem> = [
    {
      title: '#',
      key: 'index',
      width: 32,
      align: 'center',
      render: (_value, _record, index) => index + 1,
    },
    {
      title: t('cancelled_orders_reason'),
      dataIndex: 'code',
      key: 'code',
      ellipsis: true,
      render: (code: string, record) =>
        record.label || t(`cancellation_reason_${code}`, { ns: 'orders', defaultValue: code }),
    },
    {
      title: <InfoLabel label={t('cancelled_orders_share')} tooltip={t('cancelled_orders_reasons_share_tooltip')} />,
      dataIndex: 'share_percent',
      key: 'share_percent',
      width: 152,
      render: (value: number) => formatPercentValue(value, 1, i18n.language),
    },
    {
      title: (
        <InfoLabel label={t('cancelled_orders_dynamics')} tooltip={t('cancelled_orders_reasons_dynamics_tooltip')} />
      ),
      dataIndex: 'change_percent',
      key: 'change_percent',
      width: 112,
      render: (value: number | null) => <DynamicsCell value={value} suffix={ppSuffix} />,
    },
  ];

  return (
    <StyledTableCard>
      <StyledTableTitle>
        <InfoLabel label={t('cancelled_orders_reasons_title')} tooltip={t('cancelled_orders_reasons_title_tooltip')} />
      </StyledTableTitle>
      <Table
        rowKey={(row) => `${row.code}-${row.label ?? ''}`}
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        size="small"
      />
      {onViewAll ? (
        <StyledFooterButton type="default" onClick={onViewAll}>
          {t('cancelled_orders_view_all_reasons')}
        </StyledFooterButton>
      ) : null}
    </StyledTableCard>
  );
};

export default ReasonsTable;
